import type { MouseEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  Flex,
  LightModeSemanticColors,
  SecondaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { CompactAlert } from '~/components/common/CompactAlert'
import { PageLayout } from '~/components/common/PageLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import { openOrNavigate } from '~/libs/openOrNavigate'
import {
  AreaToggleRow,
  FilterToolbar,
  SpotPriceDataSourceLink,
} from '~/pages/SpotPrice/components/FilterToolbar'
import { KomaTable } from '~/pages/SpotPrice/components/KomaTable'
import { PriceChart } from '~/pages/SpotPrice/components/PriceChart'
import { StatsTable } from '~/pages/SpotPrice/components/StatsTable'
import {
  AREAS,
  DOW_JA,
  fmtYmd,
  getMaxSelectableDate,
  parseLocalYmd,
} from '~/pages/SpotPrice/constants'
import { useListSpotMarketPrices } from '~/pages/SpotPrice/hooks/useListSpotMarketPrices'
import { usePersistedSpotPriceFilters } from '~/pages/SpotPrice/hooks/usePersistedSpotPriceFilters'
import { usePersistedSpotPriceSections } from '~/pages/SpotPrice/hooks/usePersistedSpotPriceSections'
import { type SpotPriceSectionId } from '~/pages/SpotPrice/models/persistedSections'
import {
  limitPeriodEnd,
  resolveSpotPricePeriod,
} from '~/pages/SpotPrice/models/spotPricePeriod'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types 既知不具合ワークアラウンド (NormalBidTab 同形)
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

/**
 * スポット価格画面 — JEPX スポット市場の約定価格を参照する読み取り専用画面。
 *
 * design SoT: design/bidding/spot-price.html
 * 約定価格は BFF `BiddingSpotMarketService` (eSLJ の約定結果) から選択期間で取得する
 * (AC-BSP-039 / AC-BSP-046)。 取得に失敗した時は価格を空や 0 で出さず、 画面内の
 * Alert で知らせる。 集約表の天気予報・気温だけは mock の値のまま (実データ源は別途)。
 */
export const SpotPricePage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const { openSections, setOpenSections } = usePersistedSpotPriceSections()
  const navigate = useNavigate()

  // ── 受渡日フィルタ ──
  // 毎 render で再計算する (useMemo にしない)。 JEPX D-1 10:00 公開ルールで
  // getMaxSelectableDate() は 10:00 境界で today→tomorrow に変わるため、
  // 価格監視で開きっぱなしのページが境界をまたいでも date picker の max が陳腐化しない。
  const maxDate = fmtYmd(getMaxSelectableDate())
  // 期間と表示系列は供給力変化画面への遷移・再訪をまたいで保持する
  // (AC-SC-031 [P0])。保持対象と除外の理由は `models/persistedFilters.ts` 参照。
  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    visibleAreas,
    setVisibleAreas,
  } = usePersistedSpotPriceFilters()

  // ── 取得する期間 ──
  // 期間欄 (dateFrom / dateTo) から実際に取得する期間を決める。 終了日が開始日より
  // 前なら単日、 開始日から 31 日を超える分は切る (`resolveSpotPricePeriod`)。
  const period = useMemo(
    () => resolveSpotPricePeriod(dateFrom, dateTo),
    [dateFrom, dateTo],
  )
  const periodFrom = useMemo(
    () => (period === null ? null : parseLocalYmd(period.from)),
    [period],
  )
  const periodTo = useMemo(
    () => (period === null ? null : parseLocalYmd(period.to)),
    [period],
  )

  // ── 複数日判定 ──
  const isMultiDay =
    periodFrom !== null &&
    periodTo !== null &&
    periodTo.getTime() > periodFrom.getTime()

  // ── 約定価格の取得 (選択期間の全日分。 失敗は isError で画面に出す) ──
  const {
    rows: priceRows,
    isPending,
    isError,
  } = useListSpotMarketPrices(period)

  // ── コマ別表 日送りページャ ──
  // null のとき from にフォールバック
  const [komaPageDate, setKomaPageDate] = useState<Date | null>(null)

  const komaPageDateClamped = useMemo((): Date | null => {
    if (periodFrom === null || periodTo === null) return null
    let d = komaPageDate ?? periodFrom
    if (d.getTime() < periodFrom.getTime()) d = periodFrom
    if (d.getTime() > periodTo.getTime()) d = periodTo
    return d
  }, [periodFrom, periodTo, komaPageDate])

  const handleDateFromChange = useCallback(
    (v: string) => {
      const d = parseLocalYmd(v)
      const max = getMaxSelectableDate()
      const nextFrom =
        d !== null && d.getTime() > max.getTime() ? fmtYmd(max) : v
      setDateFrom(nextFrom)
      // 開始日を動かして期間が 31 日を超えるなら、 終了日を 31 日目に寄せる
      setDateTo(limitPeriodEnd(nextFrom, dateTo))
      // 期間変更でページ日リセット
      setKomaPageDate(null)
    },
    [dateTo, setDateFrom, setDateTo],
  )

  const handleDateToChange = useCallback(
    (v: string) => {
      const d = parseLocalYmd(v)
      const max = getMaxSelectableDate()
      const nextTo = d !== null && d.getTime() > max.getTime() ? fmtYmd(max) : v
      // 開始日から 31 日を超える終了日は 31 日目に寄せる (backend の上限と同じ)
      setDateTo(limitPeriodEnd(dateFrom, nextTo))
      setKomaPageDate(null)
    },
    [dateFrom, setDateTo],
  )

  const handleKomaPrev = useCallback(() => {
    if (periodFrom === null || komaPageDateClamped === null) return
    const next = new Date(
      komaPageDateClamped.getFullYear(),
      komaPageDateClamped.getMonth(),
      komaPageDateClamped.getDate() - 1,
    )
    if (next.getTime() >= periodFrom.getTime()) setKomaPageDate(next)
  }, [periodFrom, komaPageDateClamped])

  const handleKomaNext = useCallback(() => {
    if (periodTo === null || komaPageDateClamped === null) return
    const next = new Date(
      komaPageDateClamped.getFullYear(),
      komaPageDateClamped.getMonth(),
      komaPageDateClamped.getDate() + 1,
    )
    if (next.getTime() <= periodTo.getTime()) setKomaPageDate(next)
  }, [periodTo, komaPageDateClamped])

  // ── エリアフィルタ ──
  // 初期状態は全 ON (design L228-230 準拠)。既定値は保持 hook 側が持つ。

  const handleAreaToggle = useCallback((code: string, checked: boolean) => {
    setVisibleAreas((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(code)
      } else {
        next.delete(code)
      }
      return next
    })
  }, [])

  const handleMasterToggle = useCallback((checked: boolean) => {
    setVisibleAreas((prev) => {
      const next = new Set(prev)
      // master は system 以外の 9 エリアを一括 ON/OFF
      for (const a of AREAS.filter((a) => !a.isSystem)) {
        if (checked) {
          next.add(a.code)
        } else {
          next.delete(a.code)
        }
      }
      return next
    })
  }, [])

  // ── コマ別表は「ページ中の 1 日」だけを出す (期間が複数日なら日送りで切り替える) ──
  // チャートと集約表は選択期間の全日分を連結して使う。
  const pageDateYmd =
    komaPageDateClamped === null ? null : fmtYmd(komaPageDateClamped)
  const komaRows = useMemo(
    () => priceRows.filter((r) => r.date === pageDateYmd),
    [priceRows, pageDateYmd],
  )
  /* 期間の最後の受渡日に価格が無いときだけ、期間欄の下に 1 本出す (8080 #743 / Q155)。取得失敗は上の error Alert。
     最後の日は期間欄の終了日でなく、実際に取得した期間の終了日で見る (終了日が開始日より前なら開始日の単日を取得する)。 */
  const unpublishedDate =
    !isPending &&
    !isError &&
    period !== null &&
    !priceRows.some((row) => row.date === period.to)
      ? period.to
      : null
  const showKomaEmptyCopy =
    !isError &&
    !isPending &&
    komaRows.length === 0 &&
    !(unpublishedDate !== null && pageDateYmd === unpublishedDate)

  // ── 日送りページャ表示テキスト ──
  const pagerDateLabel = useMemo(() => {
    if (komaPageDateClamped === null) return ''
    const d = komaPageDateClamped
    const dow = DOW_JA[d.getDay()]
    return `${t('page:spotPrice.filter.deliveryDate')} ${fmtYmd(d).replace(/-/g, '/')} (${dow})`
  }, [komaPageDateClamped, t])

  const prevDisabled =
    periodFrom === null ||
    komaPageDateClamped === null ||
    komaPageDateClamped.getTime() <= periodFrom.getTime()

  const nextDisabled =
    periodTo === null ||
    komaPageDateClamped === null ||
    komaPageDateClamped.getTime() >= periodTo.getTime()

  const accordionBg = LightModeSemanticColors.Surface.primary.value

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.bidding') },
        { label: t('page:spotPrice.title'), bold: true },
      ]}
      // データソース (外部リンク) は絞り込みではないので、開閉式のカードに入れず画面の右上に置く (React mock と同じ)
      headerExtra={<SpotPriceDataSourceLink />}
      // 取得に失敗した時は、 価格を空や 0 で出さずに理由を出す。 ページ全体の通知なので
      // フィルタより上 (最上部) に置く。 出入りで本文が作り直されないよう、 無い時もnull を渡す。 フィルタは失敗中も触れる (期間を変えると取り直す)。
      notices={
        isError ? (
          <CompactAlert
            description={t('page:spotPrice.loadErrorDescription')}
            role="alert"
            status="error"
            title={t('page:spotPrice.loadErrorTitle')}
          />
        ) : null
      }
    >
      {/* 縦スクロール + 左右 padding (16px) は親 SideMenuLayoutContentLayout の
          content Box (overflowY:auto / padding:4) が既に提供する。 ここで
          overflowY:auto + height:100% を二重に持つと内側 VStack の scrollbar
          (padding 無し) がコンテンツ右端に被って見切れるため持たない
          (= IntradayBidding 同方針、 design は <main overflow-y:auto> 単一スクロール) */}
      <VStack align="stretch" gap={4}>
        {/* フィルタ toolbar */}
        <FilterToolbar
          dateFrom={dateFrom}
          dateTo={dateTo}
          maxDate={maxDate}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />

        {/* 表示するエリア (系列の切り替え)。開閉式のカードの外に出す (React mock と同じ。閉じたカードに入れると切り替えのたびに開くため) */}
        <AreaToggleRow
          onAreaToggle={handleAreaToggle}
          onMasterToggle={handleMasterToggle}
          visibleAreas={visibleAreas}
        />

        {/* 期間の最後の受渡日の価格が未公表の時の案内 (AC-BSP-146)。 期間欄に関わるので期間欄の下に置く
            (8080 #743 / Q155)。 短い案内なので Compact (UI 辞書 alert-placement.md §0) */}
        {unpublishedDate !== null && (
          <CompactAlert
            role="status"
            status="info"
            // 受渡日は日本語 YYYY/MM/DD、 英語 YYYY-MM-DD (AC-BSP-146)
            title={t('page:spotPrice.unpublishedAlert', {
              date: i18n.language.startsWith('en')
                ? unpublishedDate
                : unpublishedDate.replaceAll('-', '/'),
            })}
          />
        )}

        {/* ── 受渡日の約定価格 chart accordion ── */}
        {/* 初期表示で開くのは **「受渡日の約定価格」だけ** (2026-09-04 デザイナー
            判断)。 以前は chart + koma の 2 つを開いており、 下 2 つの見出しが
            画面外へ押し出されて存在に気付けなかった。 開閉は localStorage に
            保持し、 供給力変化画面へ遷移して戻っても維持する。
            既定値・検証・直列化は `models/persistedSections.ts` が SoT。 */}
        <RegionLoading isLoading={isPending}>
          <AccordionRoot
            multiple
            // chakra v3.29 + React 18 types の既知不具合で `AccordionRoot` の props が
            // `any` に解決されるため (同ファイル冒頭の AccordionItem 等と同根)、
            // 引数の形をここで明示する。 ark の contract は `{ value: string[] }`。
            onValueChange={(details: { value: string[] }) => {
              setOpenSections(details.value as SpotPriceSectionId[])
            }}
            value={openSections}
          >
            {/* 器の枠・角丸は design `css/components.css:4206` の `.ecl-accordion`
              (border 1px --border-low / radius lg) 準拠。 chakra v3 の accordion
              recipe は item に下線を引くだけで枠も角丸も持たないため、 使う側で
              明示する (NormalBidTab の同コメント参照)。 無いと閉じた状態で
              カードの枠が消える (2026-08-31 デザイナー指摘: dev 全体)。
              content の border-top は同 `:4253` `.ecl-accordion-content` 準拠。 */}
            <AccordionItem
              position="relative"
              overflow="hidden"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              backgroundColor={accordionBg}
              value="chart"
            >
              {/* trigger を包む relative wrapper。 右上のアクション overlay の縦位置基準を
                **trigger の高さそのもの**にするため (2026-09-01 デザイナー指摘
                「導線は縦センターに配置」)。 AccordionItem 直下に absolute を置くと
                開いている間は本文を含む item 全体が基準になり、 高さ固定で逃げると
                DS の trigger 実高 (56px) とズレる (旧 3rem = 48px で 4px 上寄り。
                実測: trigger 中心 45px / ボタン中心 41px)。 */}
              <Box position="relative">
                <AccordionItemTrigger paddingX={4} paddingY={3}>
                  {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
                  <AccordionDisclosureIcon />
                  <Text textStyle="sectionTitle">
                    {t('page:spotPrice.chart.title')}
                    <Text
                      as="span"
                      textStyle="captionNormal"
                      marginLeft={2}
                      color="Text.midEmphasis"
                    >
                      {t('page:spotPrice.chart.subtitle')}
                    </Text>
                  </Text>
                </AccordionItemTrigger>
                {/*
                 * アクション群は trigger (= <button>) の descendant にすると
                 * button-in-button の DOM nesting 違反になるため、 trigger の
                 * sibling として absolute overlay で右上に重ねる (= title 行と
                 * 同じ高さに見せつつ、 ボタンの click は accordion toggle に
                 * bubble しない)。
                 * 供給力変化ボタン: 単日のみ表示。 受渡日 (dateFrom) を ?delivery= で
                 * 渡して供給力変化画面へ in-app 遷移する (design decision #1681)。
                 */}
                {!isMultiDay && (
                  <Box
                    position="absolute"
                    zIndex={1}
                    top={0}
                    right={4}
                    bottom={0}
                    alignItems="center"
                    display="flex"
                  >
                    <SecondaryButton
                      onClick={(e?: MouseEvent) =>
                        openOrNavigate(
                          navigate,
                          `${routePaths.biddingSupplyCapacityChange.path}?delivery=${dateFrom}`,
                          e,
                        )
                      }
                      size="sm"
                    >
                      {t('page:spotPrice.chart.supplyCapacityChange')}
                    </SecondaryButton>
                  </Box>
                )}
              </Box>
              <AccordionItemContent
                borderTopWidth="1px"
                borderTopColor="Border.lowEmphasis"
                paddingX={3}
                paddingY={3}
              >
                <PriceChart
                  isMultiDay={isMultiDay}
                  komaRows={priceRows}
                  visibleAreas={visibleAreas}
                />
              </AccordionItemContent>
            </AccordionItem>

            {/* ── 集約表 accordion (既定 closed) ── */}
            <AccordionItem
              overflow="hidden"
              marginTop={4}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              backgroundColor={accordionBg}
              value="stats"
            >
              <AccordionItemTrigger paddingX={4} paddingY={3}>
                {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
                <AccordionDisclosureIcon />
                <Text textStyle="sectionTitle">
                  {t('page:spotPrice.stats.title')}
                  <Text
                    as="span"
                    textStyle="captionNormal"
                    marginLeft={2}
                    color="Text.midEmphasis"
                  >
                    {t('page:spotPrice.stats.subtitle')}
                  </Text>
                </Text>
              </AccordionItemTrigger>
              <AccordionItemContent
                borderTopWidth="1px"
                borderTopColor="Border.lowEmphasis"
                paddingX={3}
                paddingY={3}
              >
                <StatsTable
                  isMultiDay={isMultiDay}
                  komaRows={priceRows}
                  visibleAreas={visibleAreas}
                />
              </AccordionItemContent>
            </AccordionItem>

            {/* ── コマ別エリア価格表 accordion (既定 open) ── */}
            <AccordionItem
              position="relative"
              overflow="hidden"
              marginTop={4}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              backgroundColor={accordionBg}
              value="koma"
            >
              {/* trigger を包む relative wrapper。 右上のアクション overlay の縦位置基準を
                **trigger の高さそのもの**にするため (2026-09-01 デザイナー指摘
                「導線は縦センターに配置」)。 AccordionItem 直下に absolute を置くと
                開いている間は本文を含む item 全体が基準になり、 高さ固定で逃げると
                DS の trigger 実高 (56px) とズレる (旧 3rem = 48px で 4px 上寄り。
                実測: trigger 中心 45px / ボタン中心 41px)。 */}
              <Box position="relative">
                <AccordionItemTrigger paddingX={4} paddingY={3}>
                  {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
                  <AccordionDisclosureIcon />
                  <Text textStyle="sectionTitle">
                    {t('page:spotPrice.koma.title')}
                    <Text
                      as="span"
                      textStyle="captionNormal"
                      marginLeft={2}
                      color="Text.midEmphasis"
                    >
                      {t('page:spotPrice.koma.subtitle')}
                    </Text>
                  </Text>
                </AccordionItemTrigger>
                {/*
                 * 日送りページャは trigger (= <button>) の sibling として absolute
                 * overlay で配置 (= button-in-button 回避、 §chart accordion と同形)。
                 * 複数日選択時のみ表示。
                 */}
                {isMultiDay && (
                  <Flex
                    position="absolute"
                    zIndex={1}
                    top={0}
                    right={4}
                    bottom={0}
                    alignItems="center"
                    gap={2}
                  >
                    <SecondaryButton
                      aria-label={t('page:spotPrice.koma.prevDay')}
                      disabled={prevDisabled}
                      onClick={handleKomaPrev}
                      size="sm"
                    >
                      {/* ページ送りの ‹ › は DS icon で描く。 従来は i18n 文言に
                        `‹ ` / ` ›` を埋めていたためフォント依存で、 他画面の
                        chevron と太さ・大きさが揃わなかった。
                        design css `.btn:not(...)>svg` = 20px。 */}
                      <FaChevronLeftRegular boxSize={iconSize.labeledButton} />
                      {t('page:spotPrice.koma.prevDay')}
                    </SecondaryButton>
                    <Text
                      textStyle="label"
                      minWidth="11rem"
                      color="Text.highEmphasis"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      {pagerDateLabel}
                    </Text>
                    <SecondaryButton
                      aria-label={t('page:spotPrice.koma.nextDay')}
                      disabled={nextDisabled}
                      onClick={handleKomaNext}
                      size="sm"
                    >
                      {t('page:spotPrice.koma.nextDay')}
                      <FaChevronRightRegular boxSize={iconSize.labeledButton} />
                    </SecondaryButton>
                  </Flex>
                )}
              </Box>
              <AccordionItemContent
                padding={0}
                borderTopWidth="1px"
                borderTopColor="Border.lowEmphasis"
                paddingX={3}
                paddingY={3}
              >
                {isError || (komaRows.length === 0 && !isPending) ? (
                  showKomaEmptyCopy ? (
                    <Text
                      textStyle="body"
                      padding={4}
                      color="Text.midEmphasis"
                      textAlign="center"
                    >
                      {t('page:spotPrice.koma.empty')}
                    </Text>
                  ) : null
                ) : (
                  <KomaTable komaRows={komaRows} visibleAreas={visibleAreas} />
                )}
              </AccordionItemContent>
            </AccordionItem>
          </AccordionRoot>
        </RegionLoading>
      </VStack>
    </PageLayout>
  )
}
